/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import {
  Alert,
  Badge,
  Divider,
  Group,
  Input,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Space,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { Table } from 'antd';
import { forwardRef, useImperativeHandle } from 'react';
import { IconAlertTriangle, IconCalendar, IconInfoCircle, IconNotes } from '@tabler/icons-react';
import { ProposalStatusEnum } from '../../../../../business/proposals/status';
import ProfileCardLink from '../../../../../components/core/ProfileCardLink/ProfileCardLink';
import { ContractSearchResponseType } from '../../../../../models/core/contracts.type';
import { ProposalType } from '../../../../../models/core/proposals.type';
import theme from '../../../../../theme';
import { formatDateStringToDate, formatDateToString } from '../../../../../utils/formatter.utils';
import { now, tryGetDateISOFormat } from '../../../../../utils/helper.utils';

type FormViewData = {
  dataInicial: Date | undefined;
  dataFinal: Date | undefined;
  dataInicialJuncao: Date | undefined;
  acao: string;
  idContrato: number | null;
  observacao: string | null;
};

type FormViewProps = {
  proposal: ProposalType;
  contracts: ContractSearchResponseType[];
  newStatusCode: ProposalStatusEnum;
};

const StatusChangeForm = forwardRef((props: FormViewProps, ref) => {
  const form = useForm<FormViewData>({
    initialValues: {
      dataInicial: props.proposal
        ? formatDateStringToDate(tryGetDateISOFormat(props.proposal.dataInicial))
        : now().toDate(),
      dataFinal: props.proposal
        ? formatDateStringToDate(tryGetDateISOFormat(props.proposal.dataFinal))
        : now().toDate(),
      dataInicialJuncao: undefined,

      acao: 'N',
      idContrato: null,
      observacao: '',
    },
    validate: {
      dataInicial: (value) => {
        if (value) {
          if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
            return 'Data não suportada';
          }
        }
        return null;
      },
      dataFinal: (value, values: FormViewData) => {
        if (value) {
          if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
            return 'Data não suportada';
          }
          if (value < values.dataInicial!) {
            return 'Data final não pode ser anterior à data inicial';
          }
        }
        return null;
      },
      dataInicialJuncao: (value, values: FormViewData) => {
        if (!value && values.acao === 'J') {
          return 'Campo obrigatório';
        }
        if (value) {
          if (Math.abs(value.getFullYear() - new Date().getFullYear()) > 200) {
            return 'Data não suportada';
          }
          const contract = props.contracts.find((x) => x.idContrato === form.values.idContrato)!;
          if (value < contract.dataInicial) {
            return 'Data inicial de junção não pode ser anterior à data inicial do contrato';
          }
          if (value > contract.dataFinal) {
            return 'Data inicial de junção não pode ser posterior à data final do contrato';
          }
        }
        return null;
      },
      idContrato: (value, values: FormViewData) => {
        if ((value === null || value === undefined) && ['S', 'J'].includes(values.acao)) {
          return 'Selecione um contrato';
        }
        return null;
      },
    },
  });
  const selectedContract = props.contracts.find((x) => x.idContrato === form.values.idContrato);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: ContractSearchResponseType[]) => {
      const tempContrato = selectedRows?.[0];
      form.setFieldValue('idContrato', Number(tempContrato.idContrato) || null);
      form.setFieldValue(
        'dataInicial',
        formatDateStringToDate(tryGetDateISOFormat(tempContrato.dataInicial))
      );
      form.setFieldValue('dataFinal', formatDateStringToDate(tryGetDateISOFormat(tempContrato.dataFinal)));
      form.setFieldValue(
        'dataInicialJuncao',
        formatDateStringToDate(tryGetDateISOFormat(tempContrato.dataInicial))
      );
    },
    getCheckboxProps: (record: ContractSearchResponseType) => {
      return {
        name: record.idContrato.toString(),
      };
    },
  };

  useImperativeHandle(ref, () => ({
    validate(): FormViewData | null {
      if (props.newStatusCode === ProposalStatusEnum.Finalizado && form.validate().hasErrors) {
        return null;
      }

      const response = { ...form.values, observacao: form.values.observacao?.trim() || null };
      if (props.newStatusCode !== ProposalStatusEnum.Finalizado) {
        response.dataInicial = undefined;
        response.dataFinal = undefined;
      }

      if (form.values.acao === 'N') {
        response.idContrato = null;
      }
      if (form.values.acao !== 'J') {
        (response.dataInicialJuncao as any) = null;
      }

      return response;
    },
  }));

  return (
    <form id="status-change-form" noValidate>
      <Paper shadow="xs" p="md" withBorder>
        <div hidden={props.newStatusCode !== ProposalStatusEnum.Finalizado}>
          <SimpleGrid cols={props.contracts.length === 0 ? 2 : 4}>
            <div>
              <Input.Wrapper label="Data Inicial do Contrato" required>
                <Group
                  style={{
                    display: 'inline-block',
                    marginLeft: 5,
                    marginTop: -2,
                    verticalAlign: 'bottom',
                  }}
                >
                  <Tooltip
                    withArrow
                    transitionProps={{ transition: 'fade', duration: 200 }}
                    label="Data prevista de início do contrato"
                  >
                    <div>
                      <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                    </div>
                  </Tooltip>
                </Group>
              </Input.Wrapper>
              <DatePickerInput
                leftSection={<IconCalendar size={15} />}
                locale="pt-br"
                valueFormat="DD/MM/YYYY"
                clearable={false}
                disabled={form.values.acao === 'J'}
                required
                {...form.getInputProps('dataInicial')}
              />
            </div>

            <div>
              <Input.Wrapper label="Data Final do Contrato" required>
                <Group
                  style={{
                    display: 'inline-block',
                    marginLeft: 5,
                    marginTop: -2,
                    verticalAlign: 'bottom',
                  }}
                >
                  <Tooltip
                    withArrow
                    transitionProps={{ transition: 'fade', duration: 200 }}
                    label="Data prevista de fim do contrato"
                  >
                    <div>
                      <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                    </div>
                  </Tooltip>
                </Group>
              </Input.Wrapper>
              <DatePickerInput
                leftSection={<IconCalendar size={15} />}
                locale="pt-br"
                valueFormat="DD/MM/YYYY"
                clearable={false}
                minDate={form.values.dataInicial}
                disabled={form.values.acao === 'J'}
                required
                {...form.getInputProps('dataFinal')}
              />
            </div>

            {form.values.acao === 'J' && (
              <div>
                <Input.Wrapper label="Data Inicial da Junção" required>
                  <Group
                    style={{
                      display: 'inline-block',
                      marginLeft: 5,
                      marginTop: -2,
                      verticalAlign: 'bottom',
                    }}
                  >
                    <Tooltip
                      withArrow
                      transitionProps={{ transition: 'fade', duration: 200 }}
                      label="Data base para ajustes de itens"
                    >
                      <div>
                        <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                      </div>
                    </Tooltip>
                  </Group>
                </Input.Wrapper>
                <DatePickerInput
                  leftSection={<IconCalendar size={15} />}
                  locale="pt-br"
                  valueFormat="DD/MM/YYYY"
                  clearable={false}
                  minDate={
                    selectedContract
                      ? formatDateStringToDate(tryGetDateISOFormat(selectedContract.dataInicial))
                      : undefined
                  }
                  maxDate={
                    selectedContract
                      ? formatDateStringToDate(tryGetDateISOFormat(selectedContract.dataFinal))
                      : undefined
                  }
                  disabled={form.values.acao !== 'J'}
                  required
                  {...form.getInputProps('dataInicialJuncao')}
                />
              </div>
            )}

            {props.contracts.length > 0 && (
              <div>
                <Input.Wrapper label="Ação de Contrato" required>
                  <Group
                    style={{ display: 'inline-block', marginLeft: 5, marginTop: -2, verticalAlign: 'bottom' }}
                  >
                    <Tooltip
                      withArrow
                      transitionProps={{ transition: 'fade', duration: 200 }}
                      label={
                        <>
                          <div>Esse cliente possui contrato(s) ativo(s). O que você gostaria de fazer?</div>
                          <Divider my="sm" />
                          <div>Novo: Novo contrato gerado.</div>
                          <div>Substituição: Novo contrato gerado e contrato atual substituído.</div>
                          <div>Junção: Contrato atual atualizado.</div>
                        </>
                      }
                      multiline
                      w={475}
                    >
                      <div>
                        <IconInfoCircle size={18} color={theme?.colors?.accent?.[6]} />
                      </div>
                    </Tooltip>
                  </Group>
                </Input.Wrapper>
                <SegmentedControl
                  color="primary"
                  data={[
                    { label: 'Novo', value: 'N' },
                    { label: 'Substituição', value: 'S' },
                    { label: 'Junção', value: 'J' },
                  ]}
                  {...form.getInputProps('acao')}
                />
              </div>
            )}
          </SimpleGrid>
          <Space h="xs" />

          {['S', 'J'].includes(form.values.acao) && (
            <div>
              <SimpleGrid>
                <Paper shadow="xs" p="md" withBorder>
                  {!form.values.idContrato && (
                    <div>
                      <Alert icon={<IconAlertTriangle size={32} />} title="Atenção" color="yellow">
                        Favor selecionar um contrato para ser substituído.
                      </Alert>
                      <Space h="xs" />
                    </div>
                  )}

                  <Table
                    showSorterTooltip={false}
                    size="small"
                    dataSource={props.contracts}
                    columns={[
                      {
                        title: 'Contrato #',
                        key: 'idContrato',
                        sorter: (a: ContractSearchResponseType, b: ContractSearchResponseType) => {
                          const aValue = a.idContrato;
                          const bValue = b.idContrato;
                          if (aValue === bValue) {
                            return 0;
                          }
                          return aValue > bValue ? 1 : -1;
                        },
                        render: (row: ContractSearchResponseType) => (
                          <ProfileCardLink
                            id={row.idContrato.toString()}
                            name="Contrato"
                            nameSize="sm"
                            description={`# ${row.idContrato}`}
                            descriptionSize="xs"
                            linkPrefix="contracts"
                            showLink
                          />
                        ),
                      },
                      {
                        title: 'Proposta #',
                        key: 'idProposta',
                        sorter: (a: ContractSearchResponseType, b: ContractSearchResponseType) => {
                          const aValue = a.idProposta;
                          const bValue = b.idProposta;
                          if (aValue === bValue) {
                            return 0;
                          }
                          return aValue > bValue ? 1 : -1;
                        },
                        render: (row: ContractSearchResponseType) => (
                          <ProfileCardLink
                            id={row.idProposta.toString()}
                            name="Proposta"
                            nameSize="sm"
                            description={`# ${row.idProposta}`}
                            descriptionSize="xs"
                            linkPrefix="proposals"
                            showLink
                          />
                        ),
                      },
                      {
                        title: 'Status',
                        key: 'status',
                        sorter: (a: ContractSearchResponseType, b: ContractSearchResponseType) =>
                          a.contratoStatus.localeCompare(b.contratoStatus),
                        render: (row: ContractSearchResponseType) => (
                          <Badge variant="outline">{row.contratoStatus}</Badge>
                        ),
                      },
                      {
                        title: 'Data Inicial',
                        key: 'dataInicial',
                        sorter: (a: ContractSearchResponseType, b: ContractSearchResponseType) =>
                          new Date(a.dataInicial).valueOf() - new Date(b.dataInicial).valueOf(),
                        render: (row: ContractSearchResponseType) => formatDateToString(row.dataInicial),
                      },
                      {
                        title: 'Data Final',
                        key: 'dataFinal',
                        sorter: (a: ContractSearchResponseType, b: ContractSearchResponseType) =>
                          new Date(a.dataFinal).valueOf() - new Date(b.dataFinal).valueOf(),
                        render: (row: ContractSearchResponseType) => formatDateToString(row.dataFinal),
                      },
                    ]}
                    rowSelection={{ type: 'radio', ...rowSelection }}
                    rowKey={(item: ContractSearchResponseType) => item.idContrato}
                    pagination={{
                      pageSizeOptions: [10, 25, 50],
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]} - ${range[1]} de ${total} resultado(s)`,
                    }}
                  />
                </Paper>
              </SimpleGrid>
              <Space h="xs" />
            </div>
          )}
        </div>

        <SimpleGrid cols={1}>
          <Textarea
            leftSection={<IconNotes size={15} />}
            label="Observação"
            placeholder="Observação sobre o status"
            autosize
            maxLength={500}
            {...form.getInputProps('observacao')}
          />
        </SimpleGrid>
      </Paper>
    </form>
  );
});

export default StatusChangeForm;
